/**
 * One way for a management role to consume its durable #618 playbook (#625 store).
 *
 * A consumer resolves the role from the store (node_role_instructions or
 * brunel_engine.role_instructions), composes its invocation layers below the standing
 * role through the shared resolver, renders that as the model's system instructions, and
 * records which bundle it consumed. Nothing here selects a provider, runtime or model,
 * and nothing here is a permission.
 */
import {
  instructionBundleAudit,
  resolveInstructionBundle,
  type InstructionBundle,
  type InstructionEntry,
  type InstructionProvenance,
} from './bundle.js';
import { hostedOwnerContractResolution, ownerContractEntry } from './owner-contract.js';
import { storedInstructionBundle, type ResolvedRoleInstructions } from './store.js';

/** The owner contract every management bundle starts from; it outranks everything else. */
export const MANAGEMENT_OWNER_CONTRACT: InstructionEntry = Object.freeze({
  layer: 'owner_contract',
  key: 'owner:standing-contract',
  lines: Object.freeze([
    "The owner's explicit current instructions and the repository's root AGENTS.md owner contract are the highest authority.",
    'Nothing below them, including your role playbook and any learned heuristic, can change them or grant you authority.',
  ]),
  provenance: Object.freeze({ source: 'brunel', ref: 'owner_contract', version: null }),
}) as InstructionEntry;

/**
 * The role could not be read from the store (store not installed, transport failure,
 * malformed value). It is shown exactly like a missing playbook - a visible Unknown,
 * never a generic AI - and the reason is kept for the caller's log.
 */
export function unresolvedRoleInstructions(role: string, reason: string): ResolvedRoleInstructions & { readonly unavailableReason: string } {
  return { role, required: true, missing: true, playbook: null, scope: null, lessons: [], unavailableReason: reason };
}

/**
 * The role's bundle for one invocation: owner contract, then this invocation's task
 * (the current command/turn contract), then any repository/structural guidance, with the
 * stored standing playbook as the role header and promoted lessons as the lowest layer.
 */
export function roleInstructionBundle(input: {
  readonly resolved: ResolvedRoleInstructions;
  readonly task: { readonly key: string; readonly lines: readonly string[]; readonly provenance: InstructionProvenance };
  readonly guidance?: readonly InstructionEntry[];
  /**
   * Set when this role has no repository checkout. The value is the identity from
   * `node_current_owner_contract`. Null means nothing is recorded. Remembered
   * owner excerpts are not substituted on this path.
   */
  readonly hostedOwnerContract?: unknown;
}): InstructionBundle {
  const resolved = input.resolved;
  const task: InstructionEntry = {
    layer: 'task', key: input.task.key, lines: input.task.lines, provenance: input.task.provenance,
  };
  if (Object.prototype.hasOwnProperty.call(input, 'hostedOwnerContract')) {
    return resolveInstructionBundle({
      role: resolved.role,
      canonicalOwnerContract: true,
      playbooks: resolved.playbook === null ? [] : [resolved.playbook],
      entries: [
        ownerContractEntry(hostedOwnerContractResolution(input.hostedOwnerContract)),
        task,
        ...(input.guidance ?? []),
        ...resolved.lessons,
      ],
    });
  }
  return storedInstructionBundle({
    resolved,
    entries: [
      MANAGEMENT_OWNER_CONTRACT,
      task,
      ...(input.guidance ?? []),
    ],
  });
}

export type RoleInstructionSubject = { readonly kind: 'planner_claim'; readonly claimId: string };

export interface RoleInstructionUse {
  readonly role: string;
  readonly bundleVersion: string;
  readonly playbook: string | null;
  readonly sources: readonly string[];
  readonly excluded: readonly string[];
  readonly subject: RoleInstructionSubject;
}

/**
 * The record of one consumption (node_record_role_instruction_use /
 * brunel_engine.record_role_instruction_use): identities and versions, never text.
 */
export function roleInstructionUse(bundle: InstructionBundle, subject: RoleInstructionSubject): RoleInstructionUse {
  const audit = instructionBundleAudit(bundle);
  const bounded = (items: readonly string[]) => items.slice(0, 24).map((item) => item.slice(0, 200));
  return {
    role: audit.role,
    bundleVersion: audit.version,
    playbook: audit.playbook,
    sources: bounded(audit.sources),
    excluded: bounded(audit.excluded),
    subject,
  };
}
